import { useId, useMemo, useState } from 'react';
import { axisTicks, MARK, seriesVar } from './chartTokens';
import { compactMoney, money } from '../../lib/format';
import './charts.css';

/**
 * Proceeds by stakeholder across a range of exit values.
 *
 * Form choice: change of a magnitude across an ordered continuum, several
 * entities compared — that is a multi-series line, not a bar chart. The
 * interesting features are the *kinks*, where a preferred class flips from
 * taking its liquidation preference to converting; a line makes those visible
 * and a bar chart hides them.
 *
 * One y-axis only, in dollars. Both axes are the same unit here so there is no
 * temptation toward a second scale.
 */
export default function WaterfallCurve({ points, height = 300 }) {
  const titleId = useId();
  const [hoverIndex, setHoverIndex] = useState(null);

  const model = useMemo(() => buildModel(points), [points]);

  if (!model) {
    return <div className="chart-empty" style={{ minHeight: height }}>Not enough data to chart.</div>;
  }

  const { series, exits, maxY } = model;

  const pad = { top: 16, right: 16, bottom: 34, left: 62 };
  const width = 720;
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const xFor = (i) => pad.left + (exits.length === 1 ? plotW / 2 : (i / (exits.length - 1)) * plotW);
  const yFor = (v) => pad.top + plotH - (maxY === 0 ? 0 : (v / maxY) * plotH);

  const yTicks = axisTicks(maxY, 5);
  const active = hoverIndex === null ? null : hoverIndex;

  return (
    <div className="chart-block">
      <svg
        className="linechart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby={titleId}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <title id={titleId}>Proceeds to each stakeholder across exit values</title>

        {/* Recessive grid */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={yFor(tick)}
              y2={yFor(tick)}
              className="grid-line"
            />
            <text x={pad.left - 8} y={yFor(tick) + 4} className="axis-label" textAnchor="end">
              {compactMoney(tick)}
            </text>
          </g>
        ))}

        {/* X labels — thinned so they never collide */}
        {exits.map((exit, i) => {
          const step = Math.ceil(exits.length / 7);
          if (i % step !== 0 && i !== exits.length - 1) return null;
          return (
            <text key={exit} x={xFor(i)} y={height - 12} className="axis-label" textAnchor="middle">
              {compactMoney(exit)}
            </text>
          );
        })}

        {/* Crosshair for the hovered exit value */}
        {active !== null && (
          <line
            x1={xFor(active)}
            x2={xFor(active)}
            y1={pad.top}
            y2={pad.top + plotH}
            className="crosshair"
          />
        )}

        {/* Series lines. 2px, no fill — overlapping areas would obscure the kinks. */}
        {series.map((s, index) => (
          <path
            key={s.key}
            d={s.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(v)}`).join(' ')}
            fill="none"
            stroke={seriesVar(index)}
            strokeWidth={MARK.lineWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Markers only on the hovered column, each with a surface ring so
            overlapping points stay distinguishable. */}
        {active !== null &&
          series.map((s, index) => (
            <circle
              key={`${s.key}-dot`}
              cx={xFor(active)}
              cy={yFor(s.values[active])}
              r={MARK.markerRadius}
              fill={seriesVar(index)}
              stroke="var(--surface-1)"
              strokeWidth={MARK.ringWidth}
            />
          ))}

        {/* Invisible hit bands, wider than the marks */}
        {exits.map((exit, i) => (
          <rect
            key={`hit-${exit}`}
            x={xFor(i) - plotW / Math.max(exits.length - 1, 1) / 2}
            y={pad.top}
            width={plotW / Math.max(exits.length - 1, 1)}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHoverIndex(i)}
          />
        ))}
      </svg>

      {active !== null && (
        <div className="chart-tooltip" role="status">
          <div className="tooltip-head">Exit at {money(exits[active], { places: 0 })}</div>
          <table>
            <tbody>
              {series
                .map((s, index) => ({ s, index, v: s.values[active] }))
                .sort((a, b) => b.v - a.v)
                .map(({ s, index, v }) => (
                  <tr key={s.key}>
                    <td>
                      <span className="swatch" style={{ background: seriesVar(index) }} aria-hidden="true" />
                      {s.label}
                    </td>
                    <td className="num">{money(String(v), { places: 0 })}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <ul className="legend-row">
        {series.map((s, index) => (
          <li key={s.key}>
            <span className="swatch" style={{ background: seriesVar(index) }} aria-hidden="true" />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Reshape the API's per-exit rows into per-stakeholder series.
 *
 * Numbers become JS floats here, which is acceptable and deliberate: this is
 * chart *geometry* and axis labelling only. Every figure shown in a tooltip or
 * table elsewhere comes from the API's exact strings.
 */
function buildModel(points) {
  if (!Array.isArray(points) || points.length < 2) return null;

  const exits = points.map((p) => Number(p.exitProceeds));
  const byStakeholder = new Map();

  points.forEach((point, pointIndex) => {
    for (const holder of point.stakeholders ?? []) {
      if (!byStakeholder.has(holder.stakeholderId)) {
        byStakeholder.set(holder.stakeholderId, {
          key: holder.stakeholderId,
          label: holder.name,
          values: new Array(points.length).fill(0),
          peak: 0,
        });
      }
      const series = byStakeholder.get(holder.stakeholderId);
      const value = Number(holder.total);
      series.values[pointIndex] = value;
      series.peak = Math.max(series.peak, value);
    }
  });

  // Rank by peak payout so slot assignment is stable and meaningful, then fold
  // past the eighth into nothing (dropped rather than mis-coloured) — the table
  // below the chart carries every holder.
  const ranked = [...byStakeholder.values()].sort((a, b) => b.peak - a.peak).slice(0, 8);
  const maxY = Math.max(...ranked.flatMap((s) => s.values), 0);

  return { series: ranked, exits, maxY };
}
